import { FC } from "react";
import { IonContent, IonModal, IonPage, IonText } from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import { useTranslation } from "react-i18next";
import HeaderComponent from "./HeaderComponent";
import { useIsOnline } from "./hooks";
import { VimeoVideo } from "../constants/urls";
import "./HowToVideoModal.css";

interface HowToVideoModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    video: VimeoVideo;
    title: string;
    // Drives the modal's own aspect ratio on larger screens, so the video
    // fills it rather than being cropped. Defaults to landscape (16:9).
    orientation?: "portrait" | "landscape";
}

export function buildVimeoEmbedUrl(video: VimeoVideo): string {
    const params = new URLSearchParams({
        badge: "0",
        autopause: "0",
        player_id: "0",
        app_id: "58479",
        autoplay: "1",
        muted: "1",
        // Player chrome we do not want in a tutorial. These four are the only
        // ones an embed parameter can force - the Like button, the Embed/share
        // button, the Vimeo logo and the end-of-video "outro" recommendations
        // ignore embed parameters entirely and can only be turned off in the
        // video's own settings on Vimeo (or by using background=1, which also
        // strips play/pause and locks the video muted).
        title: "0",
        byline: "0",
        portrait: "0",
        pip: "0",
    });

    if (video.hash) {
        params.set("h", video.hash);
    }

    return `https://player.vimeo.com/video/${video.id}?${params.toString()}`;
}

const HowToVideoModal: FC<HowToVideoModalProps> = ({
    isOpen,
    onDismiss,
    video,
    title,
    orientation = "landscape",
}) => {
    const { t } = useTranslation();
    const isOnline = useIsOnline();

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onDismiss}
            showBackdrop
            className={`how-to-video-modal how-to-video-modal-${orientation}`}
        >
            <IonPage>
                <HeaderComponent
                    title={title}
                    titleTestId="howToVideoTitle"
                    onClickEndButton={onDismiss}
                    endBtnIconName={closeOutline}
                    endBtnTestId="howToVideoCloseBtn"
                />
                <IonContent className="how-to-video-content">
                    <div className="how-to-video-body">
                        {isOnline ? (
                            <div className="how-to-video-frame">
                                <iframe
                                    src={buildVimeoEmbedUrl(video)}
                                    frameBorder="0"
                                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                                    allowFullScreen
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    title={title}
                                    data-testid="howToVideoIframe"
                                ></iframe>
                            </div>
                        ) : (
                            <IonText
                                className="how-to-video-offline"
                                data-testid="howToVideoOffline"
                            >
                                <p>{t("help.OnboardingVideo.noInternet")}</p>
                            </IonText>
                        )}
                    </div>
                </IonContent>
            </IonPage>
        </IonModal>
    );
};

export default HowToVideoModal;
